import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Tag } from 'lucide-react';
import axios from 'axios';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get('/api/blog/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      // Fallback to mock data
      setPosts(mockPosts);
    } finally {
      setLoading(false);
    }
  };

  const mockPosts = [
    {
      _id: '1',
      title: 'Getting Started with Web3 Development',
      excerpt: 'A comprehensive guide to building your first decentralized application.',
      slug: 'getting-started-web3',
      tags: ['Blockchain', 'Web3', 'Tutorial'],
      createdAt: '2024-01-20',
      author: { name: 'Admin' }
    },
    {
      _id: '2',
      title: 'React Performance Optimization Tips',
      excerpt: 'Learn how to make your React applications faster and more efficient.',
      slug: 'react-performance-tips',
      tags: ['React', 'Performance', 'JavaScript'],
      createdAt: '2024-01-18',
      author: { name: 'Admin' }
    },
    {
      _id: '3',
      title: 'Understanding AI Ethics in Modern Applications',
      excerpt: 'Exploring the ethical considerations when integrating AI into your products.',
      slug: 'ai-ethics',
      tags: ['AI', 'Ethics', 'Technology'],
      createdAt: '2024-01-15',
      author: { name: 'Admin' }
    }
  ];

  const allTags = [...new Set(posts.flatMap(post => post.tags))];

  const filteredPosts = selectedTag
    ? posts.filter(post => post.tags.includes(selectedTag))
    : posts;

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(var(--color-accent))]"></div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24">
      <div className="container-custom max-w-6xl">
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center mb-12"
        >
          <h1 className="mb-6">Blog</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Thoughts, tutorials, and insights on technology, development, and innovation.
          </p>
        </motion.div>

        {/* Tags Filter */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <button
              onClick={() => setSelectedTag(null)}
              className={`px-4 py-2 rounded-lg transition-all ${
                !selectedTag
                  ? 'bg-[rgb(var(--color-accent))] text-white'
                  : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              All Posts
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-4 py-2 rounded-lg transition-all ${
                  selectedTag === tag
                    ? 'bg-[rgb(var(--color-accent))] text-white'
                    : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Blog Posts */}
        <div className="space-y-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">No posts found.</p>
            </div>
          ) : (
            filteredPosts.map((post, index) => (
              <motion.article
                key={post._id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ delay: index * 0.1 }}
                className="card"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-grow">
                    <div className="flex items-center gap-2 mb-3">
                      {post.tags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm"
                        >
                          <Tag size={14} className="mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <Link to={`/blog/${post.slug}`}>
                      <h2 className="text-3xl font-display font-semibold mb-3 hover:text-[rgb(var(--color-accent))] transition-colors">
                        {post.title}
                      </h2>
                    </Link>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-4 text-lg">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-500">
                      <Calendar size={16} className="mr-2" />
                      <span>{new Date(post.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      <span className="mx-2">•</span>
                      <span>By {post.author?.name || 'Admin'}</span>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;
